import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Button, Menu } from "@mantine/core"
import {
  IconCamera,
  IconChevronDown,
  IconDeviceLaptop,
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconSquareFilled,
  IconSquareRoundedFilled,
} from "@tabler/icons-react"
import { useCallback, useMemo, useRef, useState } from "react"
import { uploadMeetingMaterial } from "../../lib/api"
import { extractErrorMessage } from "../../lib/api/errors"
import { formatClock } from "../../lib/format-date"
import { notifyError } from "../../lib/notify"
import type { IAudioPipelineHandle } from "./interfaces"
import { MeterBar } from "./MeterBar"
import classes from "./RecordingBar.module.css"
import type { TMicLevel } from "./Visualizer"

interface IProps {
  paused: boolean
  elapsed: number
  level: TMicLevel
  audio: IAudioPipelineHandle
  onPause: () => void
  onResume: () => void
  onFinish: () => void
  /** Live meeting id — enables the snap control. */
  meetingId?: string
  /** Hide Pause/Resume, Snap, and Boost; Finish becomes the filled "Stop recording" primary. */
  compact?: boolean
  /** Optional language status, rendered in the middle column next to the device chip. */
  languagePill?: React.ReactNode
}

export function RecordingBar({
  paused,
  elapsed,
  level,
  audio,
  onPause,
  onResume,
  onFinish,
  meetingId,
  compact = false,
  languagePill,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const snapRef = useRef<HTMLInputElement>(null)
  const [snapCount, setSnapCount] = useState(0)
  const [snapBusy, setSnapBusy] = useState(false)

  const handleSnapFiles = useCallback(
    async (files: FileList | null): Promise<void> => {
      if (!meetingId || !files || files.length === 0) return
      setSnapBusy(true)
      try {
        for (const file of Array.from(files)) {
          await uploadMeetingMaterial(meetingId, file, {
            kind: "capture",
            origin: "camera",
            capturedAtSeconds: elapsed,
          })
          setSnapCount((n) => n + 1)
        }
      } catch (err) {
        notifyError(extractErrorMessage(err))
      } finally {
        setSnapBusy(false)
      }
    },
    [elapsed, meetingId],
  )

  const selectedDeviceLabel = useMemo(() => {
    const dev = audio.devices.find((d) => d.deviceId === audio.selectedDevice)
    return dev?.label || i18n._(msg`Default microphone`)
  }, [audio.devices, audio.selectedDevice, i18n])

  // Effective volume the meter should render. When paused, force silent so the
  // bar reads 4% per spec.
  const effectiveVolume = paused ? 0 : audio.volume
  const effectiveLevel: TMicLevel = paused ? "silent" : level

  return (
    <div
      className={classes.bar}
      data-paused={paused ? "true" : "false"}
      data-compact={compact ? "true" : "false"}
      data-testid="recording-bar"
    >
      <div className={classes.timer}>
        <span className={classes.timerLabel}>
          <span className={classes.dot} />
          {paused ? <Trans>Paused</Trans> : <Trans>Recording</Trans>}
        </span>
        <span className={classes.timerValue} data-testid="recording-timer">
          {formatClock(elapsed)}
        </span>
      </div>

      <div className={classes.mic}>
        <div className={classes.micHead}>
          <div className={classes.micHeadStart}>
            <Menu shadow="md" width={280} position="bottom-start" disabled={audio.devices.length === 0}>
              <Menu.Target>
                <button type="button" className={classes.deviceChip}>
                  <IconDeviceLaptop size={14} />
                  <span className={classes.deviceChipName}>{selectedDeviceLabel}</span>
                  <IconChevronDown size={13} />
                </button>
              </Menu.Target>
              <Menu.Dropdown>
                {audio.devices.map((d) => (
                  <Menu.Item key={d.deviceId} onClick={() => audio.setSelectedDevice(d.deviceId)}>
                    {d.label || i18n._(msg`Microphone`)}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
            {languagePill}
          </div>

          {!compact && (
            <div className={classes.settingsRow}>
              <button
                type="button"
                className={classes.pillToggle}
                data-on={audio.boostEnabled ? "true" : "false"}
                onClick={() => audio.handleBoostToggle(!audio.boostEnabled)}
              >
                <span className={classes.pillDot} />
                <Trans>Boost</Trans>
              </button>
            </div>
          )}
        </div>

        <div className={classes.meterRow}>
          <span className={classes.meterLabel} aria-hidden="true">
            <Trans>Mic level</Trans>
          </span>
          <div className={classes.meterTrack}>
            <MeterBar volume={effectiveVolume} level={effectiveLevel} />
          </div>
        </div>
      </div>

      <div className={classes.controls}>
        {compact ? (
          <Button
            color="brand"
            radius="xl"
            className={classes.stopBtn}
            leftSection={<IconSquareFilled size={14} />}
            onClick={onFinish}
            data-testid="stop-recording"
          >
            <Trans>Stop recording</Trans>
          </Button>
        ) : (
          <>
            {paused ? (
              <button
                type="button"
                className={`${classes.cbtn} ${classes.resumeBtn}`}
                onClick={onResume}
                data-testid="recording-resume"
              >
                <IconPlayerPlayFilled size={14} />
                <Trans>Resume</Trans>
              </button>
            ) : (
              <button
                type="button"
                className={`${classes.cbtn} ${classes.pauseBtn}`}
                onClick={onPause}
                data-testid="recording-pause"
              >
                <IconPlayerPauseFilled size={14} />
                <Trans>Pause</Trans>
              </button>
            )}
            {meetingId && (
              <>
                <button
                  type="button"
                  className={`${classes.cbtn} ${classes.snapBtn}`}
                  disabled={snapBusy}
                  aria-label={i18n._(msg`Snap whiteboard`)}
                  onClick={() => snapRef.current?.click()}
                >
                  <IconCamera size={14} />
                  <Trans>Snap</Trans>
                  {snapCount > 0 ? ` ${snapCount}` : ""}
                </button>
                <input
                  ref={snapRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className={classes.hiddenInput}
                  onChange={(e) => {
                    void handleSnapFiles(e.currentTarget.files)
                    e.currentTarget.value = ""
                  }}
                />
              </>
            )}
            <button
              type="button"
              className={`${classes.cbtn} ${classes.finishBtn}`}
              onClick={onFinish}
              data-testid="recording-finish"
            >
              <IconSquareRoundedFilled size={14} />
              <Trans>Stop recording</Trans>
            </button>
          </>
        )}
      </div>
    </div>
  )
}
